import { Info } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { ContainerKind } from "@/lib/arch-parser";
import { cn } from "@/lib/utils";
import { useArchViz } from "./ArchVizContext";
import { containerPalette } from "./nodes/node-colors";

const CONTAINER_LEGEND: { kind: ContainerKind; label: string }[] = [
  { kind: "frontend", label: "Frontend" },
  { kind: "service", label: "Service / API" },
  { kind: "database", label: "Database" },
  { kind: "cache", label: "Cache" },
  { kind: "queue", label: "Queue / Worker" },
  { kind: "gateway", label: "Gateway" },
  { kind: "storage", label: "Storage" },
  { kind: "ml-model", label: "ML model" },
];

const LEVEL_HELP: Record<1 | 2 | 3, { what: string; why: string; audience: string }> = {
  1: {
    what: "A bird's-eye view of the system as a single box, surrounded by the people who use it and the other systems it talks to.",
    why: "Sets the scope and shows how this system fits into its world — no internal detail.",
    audience: "Everyone: business stakeholders, product, support, and engineering.",
  },
  2: {
    what: "Zooms into the system to show its containers — separately deployable units like a web app, API, database, queue, or worker — and how they communicate.",
    why: "Surfaces the high-level technology choices and runtime shape of the system.",
    audience: "Technical: developers, architects, operations, security.",
  },
  3: {
    what: "Zooms into a single container to show the components inside it (groupings of related code) and their relationships.",
    why: "Explains how a container is structured internally and where responsibilities live.",
    audience: "Developers and architects working in or around that container.",
  },
};

function Swatch({ classNames }: { classNames: string }) {
  return <span className={cn("inline-block w-3 h-3 rounded-sm border", classNames)} />;
}

interface LegendDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LegendDialog({ open, onOpenChange }: LegendDialogProps) {
  const { state } = useArchViz();
  const levelLabel =
    state.drillLevel === 1
      ? "Level 1 · System Context"
      : state.drillLevel === 2
        ? "Level 2 · Containers"
        : "Level 3 · Components";
  const help = LEVEL_HELP[state.drillLevel];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Info className="h-4 w-4 text-muted-foreground" />
            {levelLabel}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="space-y-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                What this level shows
              </div>
              <p className="text-foreground/90 leading-snug">{help.what}</p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Why it exists
              </div>
              <p className="text-foreground/90 leading-snug">{help.why}</p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Audience
              </div>
              <p className="text-foreground/90 leading-snug">{help.audience}</p>
            </div>
            <div className="text-[10px] text-muted-foreground pt-1">
              Diagrams follow the{" "}
              <a
                href="https://c4model.com"
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-foreground"
              >
                C4 model
              </a>{" "}
              by Simon Brown.
            </div>
          </div>

          <div className="border-t pt-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
              Node types
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Swatch classNames="bg-[var(--node-actor-fill)] border-[var(--node-actor-border)] rounded-full" />
                <span>Actor (person or system)</span>
              </div>
              <div className="flex items-center gap-2">
                <Swatch classNames="bg-[var(--node-external-fill)] border-[var(--node-external-border)] border-dashed" />
                <span>External system</span>
              </div>
              <div className="flex items-center gap-2">
                <Swatch classNames="bg-background border-foreground/40 border-dashed" />
                <span>System boundary</span>
              </div>
            </div>
          </div>

          {state.drillLevel >= 2 && (
            <div className="border-t pt-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
                Container types
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                {CONTAINER_LEGEND.map(({ kind, label }) => {
                  const palette = containerPalette(kind);
                  return (
                    <div key={kind} className="flex items-center gap-2">
                      <Swatch classNames={cn(palette.bg, palette.border)} />
                      <span>{label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="border-t pt-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
              Edges
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <svg width="32" height="8" viewBox="0 0 32 8" className="text-foreground/60">
                  <title>connects-to edge</title>
                  <line x1="0" y1="4" x2="28" y2="4" stroke="currentColor" strokeWidth="1.5" />
                  <polygon points="28,0 32,4 28,8" fill="currentColor" />
                </svg>
                <span>connects_to</span>
              </div>
              <div className="flex items-center gap-2">
                <svg width="32" height="8" viewBox="0 0 32 8" className="text-muted-foreground">
                  <title>actor edge</title>
                  <line
                    x1="0"
                    y1="4"
                    x2="28"
                    y2="4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeDasharray="3 3"
                  />
                  <polygon points="28,0 32,4 28,8" fill="currentColor" />
                </svg>
                <span>actor → container</span>
              </div>
            </div>
          </div>

          {state.overlay !== "none" && (
            <div className="border-t pt-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
                Overlay
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    "inline-block w-3 h-3 rounded-sm ring-2",
                    state.overlay === "cost_impact" && "ring-amber-400/70",
                    state.overlay === "scale_performance" && "ring-orange-400/70",
                    state.overlay === "security" && "ring-red-400/70",
                    state.overlay === "compliance" && "ring-blue-400/70",
                    state.overlay === "data_readiness" && "ring-purple-400/70",
                    state.overlay === "data_connectivity" && "ring-teal-400/70",
                  )}
                />
                <span>{state.overlay.replace(/_/g, " ")} highlight</span>
              </div>
            </div>
          )}

          <div className="text-[10px] text-muted-foreground pt-1 border-t space-y-1">
            <div>
              <span className="font-medium">Tip:</span> double-click the system boundary or a
              container to drill in.
            </div>
            <div>
              External systems with documented API surface (permissions + endpoints) also drill —
              look for the pointer cursor on hover.
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
